/**
 * /districts/ Map + List (sites#467). The map draws one chamber at a time:
 * Congressional, State Senate or State House. A state click zooms in and loads
 * that state's boundaries for the chamber; a district click opens its page.
 * AK / HI / PR are relocated and DC gets a marker at the national zoom
 * (electinfo/rundeck#852).
 *
 * Geometry is the simplified per-state TIGER/Line collections GeoShapeImageSync
 * publishes to the static-assets bucket (served under /geo/):
 *   Congressional /geo/districts/us-districts-<st>.geojson (properties.district_id)
 *   State Senate  /geo/sldu/us-sldu-<st>.geojson           (properties.state, code, name, geoid)
 *   State House   /geo/sldl/us-sldl-<st>.geojson
 * A state legislative polygon is keyed to the serving graph's District by state
 * and Census code (the graph's district_number); the link is the District's
 * canonical slug, its UUID storage path when the slug is not canonical-shaped,
 * and no link when the serving graph has no such District (a graph without SLDs).
 *
 * The chamber is the list's `tab` URL parameter (All draws Congressional), so
 * Map and List show the same category; `view=map` and `state=XX` deep-link.
 */
import { canonicalPath } from '@electinfo/common';
import { BROWSE_ENTITIES_QUERY } from './browse-query';
import { sldDisplayName, sldSlug, SLD_LABEL, type SLDKind } from './district-labels';

declare const L: any;

export type MapKind = 'cd' | SLDKind;

export const MAP_CHAMBERS: Array<{ kind: MapKind; tab: string; label: string }> = [
  { kind: 'cd', tab: 'congressional', label: 'Congressional' },
  { kind: 'sldu', tab: 'state-senate', label: 'State Senate' },
  { kind: 'sldl', tab: 'state-house', label: 'State House' },
];

export const STATE_NAMES: Record<string, string> = {
  al: 'Alabama', ak: 'Alaska', az: 'Arizona', ar: 'Arkansas', ca: 'California',
  co: 'Colorado', ct: 'Connecticut', de: 'Delaware', dc: 'District of Columbia',
  fl: 'Florida', ga: 'Georgia', hi: 'Hawaii', id: 'Idaho', il: 'Illinois',
  in: 'Indiana', ia: 'Iowa', ks: 'Kansas', ky: 'Kentucky', la: 'Louisiana',
  me: 'Maine', md: 'Maryland', ma: 'Massachusetts', mi: 'Michigan', mn: 'Minnesota',
  ms: 'Mississippi', mo: 'Missouri', mt: 'Montana', ne: 'Nebraska', nv: 'Nevada',
  nh: 'New Hampshire', nj: 'New Jersey', nm: 'New Mexico', ny: 'New York',
  nc: 'North Carolina', nd: 'North Dakota', oh: 'Ohio', ok: 'Oklahoma',
  or: 'Oregon', pa: 'Pennsylvania', pr: 'Puerto Rico', ri: 'Rhode Island', sc: 'South Carolina',
  sd: 'South Dakota', tn: 'Tennessee', tx: 'Texas', ut: 'Utah', vt: 'Vermont',
  va: 'Virginia', wa: 'Washington', wv: 'West Virginia', wi: 'Wisconsin', wy: 'Wyoming',
};

/** The chamber the map draws for a list tab: All (or none) draws Congressional. */
export function kindForTab(tab: string | null): MapKind {
  return MAP_CHAMBERS.find(c => c.tab === tab)?.kind ?? 'cd';
}

/** The per-state boundary collection for a chamber. */
export function geometryURL(kind: MapKind, stateCode: string): string {
  const st = stateCode.toLowerCase();
  return kind === 'cd' ? `/geo/districts/us-districts-${st}.geojson` : `/geo/${kind}/us-${kind}-${st}.geojson`;
}

/** Why a state has no boundaries for a chamber (no file is published for these). */
export function noChamberMessage(kind: MapKind, stateCode: string): string {
  const st = stateCode.toLowerCase();
  const name = STATE_NAMES[st] || stateCode.toUpperCase();
  if (kind === 'sldl' && st === 'ne') return 'Nebraska has a one-house (unicameral) legislature: see State Senate.';
  if (kind === 'sldl' && st === 'dc') return 'The District of Columbia has no State House: its council wards are under State Senate.';
  return `No ${kind === 'cd' ? 'Congressional' : SLD_LABEL[kind as SLDKind]} boundaries are published for ${name}.`;
}

export interface GraphDistrict { id: string; slug: string }

/** A BrowseEntities row's inventory key and link target, or null for a row of another kind/state. */
export function inventoryEntry(row: { id: string; state?: string | null; district?: string | null; metadata?: string | null }, kind: SLDKind, stateCode: string): [string, GraphDistrict] | null {
  let metadata: { district_type?: string; district_slug?: string } = {};
  try { metadata = JSON.parse(row.metadata || '{}') || {}; } catch { /* no metadata */ }
  if (metadata.district_type !== kind || String(row.state || '').toUpperCase() !== stateCode.toUpperCase()) return null;
  const code = String(row.district || '').toUpperCase();
  if (!code) return null;
  return [code, { id: row.id, slug: metadata.district_slug || '' }];
}

/**
 * The page a state legislative polygon opens: the canonical slug when the graph's
 * District carries it, else that District's UUID storage path. Without an
 * inventory (the API was unreachable) the slug rule alone, which is the graph's.
 * Null when the serving graph has no such District.
 */
export function sldHref(props: { state?: string; code?: string }, kind: SLDKind, inventory: Map<string, GraphDistrict> | null): string | null {
  const state = String(props.state || '').toUpperCase();
  const code = String(props.code || '').toUpperCase();
  const slug = sldSlug(state, kind, code);
  if (!slug) return null;
  if (!inventory) return `/districts/${slug}/`;
  const district = inventory.get(code);
  if (!district) return null;
  if (district.slug.toLowerCase() === slug) return `/districts/${slug}/`;
  return `/${canonicalPath('District', encodeURIComponent(district.id))}/`;
}

/** Tooltip text for a state legislative polygon. */
export function sldTooltip(props: { state?: string; code?: string; name?: string }, kind: SLDKind, href: string | null): string {
  const name = sldDisplayName(STATE_NAMES[String(props.state || '').toLowerCase()] || '', kind, String(props.code || ''), props.name);
  return href ? name : `${name} (page not yet available)`;
}

const INVENTORY_MAX_PAGES = 10;  // 500 rows; NH's 164 House districts are the most per state

/** Every District of a chamber in a state, from the serving graph (null when the API fails). */
export async function fetchInventory(kind: SLDKind, stateCode: string, fetchImpl: typeof fetch = fetch): Promise<Map<string, GraphDistrict> | null> {
  const inventory = new Map<string, GraphDistrict>();
  try {
    for (let page = 1; page <= INVENTORY_MAX_PAGES; page++) {
      const res = await fetchImpl('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: BROWSE_ENTITIES_QUERY, variables: {
          entityType: 'District', page, sortOrder: 'ASC', sortField: 'district_slug',
          filter: `district_type:${kind},state:${stateCode.toUpperCase()}`,
        } }),
      });
      if (!res.ok) return null;
      const body = await res.json();
      const data = body?.data?.browseEntities;
      if (body?.errors || !data) return null;
      for (const row of data.results || []) {
        const entry = inventoryEntry(row, kind, stateCode);
        if (entry) inventory.set(entry[0], entry[1]);
      }
      if (!data.hasMore) return inventory;
    }
    return inventory;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Page wiring (browser only)

const DEFAULT_STYLE = { fillColor: '#e2e8f0', weight: 1.2, color: '#2c5282', fillOpacity: 0.3 };
const HOVER_STYLE = { fillColor: '#ed8936', weight: 2.5, color: '#1a365d', fillOpacity: 0.55 };
const UNLINKED_STYLE = { fillColor: '#cbd5e0', weight: 1, color: '#718096', fillOpacity: 0.2, dashArray: '3 3' };

class DistrictMap {
  private map: any = null;
  private statesLayer: any = null;
  private districtLayer: any = null;
  private statesGeoJson: any = null;
  private kind: MapKind = 'cd';
  private currentState: { code: string; name: string; bounds: any } | null = null;
  private loadSeq = 0;
  private backBtnEl: HTMLElement | null = null;
  private stateLabelEl: HTMLElement | null = null;
  private noticeEl: HTMLElement | null = null;
  private insetGroup: any = null;
  private insetZoomHooked = false;
  private pendingState: string | null = null;

  constructor(private readonly container: HTMLElement, private readonly chamberBar: HTMLElement) {
    chamberBar.innerHTML = MAP_CHAMBERS.map(c =>
      `<button type="button" class="map-chamber-btn" data-kind="${c.kind}" aria-pressed="false">${c.label}</button>`).join('');
    chamberBar.querySelectorAll<HTMLButtonElement>('.map-chamber-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const chamber = MAP_CHAMBERS.find(c => c.kind === btn.dataset.kind)!;
        this.setKind(chamber.kind);
        writeURL({ tab: chamber.tab });
      });
    });
  }

  /** Show the map for the URL's chamber and state. */
  show(): void {
    const params = new URLSearchParams(location.search);
    const kind = kindForTab(params.get('tab'));
    const state = (params.get('state') || '').toLowerCase();
    if (!this.map) {
      this.kind = kind;
      this.syncChamberBar();
      this.pendingState = STATE_NAMES[state] ? state : null;
      this.init();
      return;
    }
    this.map.invalidateSize();
    if (kind !== this.kind) this.setKind(kind);
  }

  private setKind(kind: MapKind): void {
    this.kind = kind;
    this.syncChamberBar();
    if (this.currentState) this.loadDistricts(this.currentState.code);
  }

  private syncChamberBar(): void {
    this.chamberBar.querySelectorAll<HTMLElement>('.map-chamber-btn').forEach(btn => {
      const active = btn.dataset.kind === this.kind;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
    this.container.dataset.kind = this.kind;
  }

  private init(): void {
    this.map = L.map('map', { center: [39.8, -98.5], zoom: 4, minZoom: 3, maxZoom: 14 });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);
    this.loadStates().then(data => {
      if (!data) return;
      this.showStatesView();
      const pending = this.pendingState;
      this.pendingState = null;
      if (pending) this.realStateLayer(pending)?.fire('click');
    });
  }

  private loadStates(): Promise<any> {
    if (this.statesGeoJson) return Promise.resolve(this.statesGeoJson);
    return fetch('/geo/states/us-states.geojson')
      .then(r => { if (!r.ok) throw new Error('GeoJSON unavailable'); return r.json(); })
      .then(data => { this.statesGeoJson = data; return data; })
      .catch(err => { console.warn('Could not load state boundaries:', err); return null; });
  }

  private showStatesView(): void {
    this.loadSeq++;
    if (this.districtLayer) { this.map.removeLayer(this.districtLayer); this.districtLayer = null; }
    if (this.statesLayer) { this.map.removeLayer(this.statesLayer); this.statesLayer = null; }
    this.removeMapControls();
    this.currentState = null;
    const defaultStyle = { fillColor: '#e2e8f0', weight: 1.5, color: '#1a365d', fillOpacity: 0.35 };
    const self = this;
    this.statesLayer = L.geoJSON(this.statesGeoJson, {
      style: () => defaultStyle,
      onEachFeature: (feature: any, layer: any) => {
        const code = feature.properties.code;
        const name = STATE_NAMES[code] || feature.properties.name;
        layer.bindTooltip(name + ' — click to view districts', { sticky: true, direction: 'top' });
        layer.on('mouseover', function (this: any) {
          this.setStyle(HOVER_STYLE);
          this.bringToFront();
          this.getElement().style.cursor = 'pointer';
        });
        layer.on('mouseout', function (this: any) { self.statesLayer?.resetStyle(this); });
        layer.on('click', function (this: any) { self.drillIntoState(code, name, this.getBounds()); });
      },
    }).addTo(this.map);
    this.addInsets();
    this.map.setView([39.8, -98.5], 4);
  }

  private drillIntoState(code: string, name: string, bounds: any): void {
    this.removeInsets();
    if (this.statesLayer) { this.map.removeLayer(this.statesLayer); this.statesLayer = null; }
    this.currentState = { code, name, bounds };
    this.map.fitBounds(bounds, { padding: [30, 30] });
    this.addMapControls(name);
    writeURL({ state: code.toUpperCase() });
    this.loadDistricts(code);
  }

  private loadDistricts(code: string): void {
    const seq = ++this.loadSeq;
    const kind = this.kind;
    if (this.districtLayer) { this.map.removeLayer(this.districtLayer); this.districtLayer = null; }
    this.setNotice(null);
    this.container.dataset.loaded = '';
    const geometry = fetch(geometryURL(kind, code)).then(r => {
      if (r.status === 404) return null;
      if (!r.ok) throw new Error('District GeoJSON unavailable');
      return r.json();
    });
    const inventory = kind === 'cd' ? Promise.resolve(null) : fetchInventory(kind, code);
    Promise.all([geometry, inventory]).then(([data, graph]) => {
      if (seq !== this.loadSeq) return;  // a newer state or chamber was chosen meanwhile
      if (!data) { this.setNotice(noChamberMessage(kind, code)); this.container.dataset.loaded = kind; return; }
      this.showDistrictsView(data, kind, graph);
    }).catch(err => {
      if (seq !== this.loadSeq) return;
      console.warn('Could not load district boundaries for', code, err);
      this.showStatesView();
    });
  }

  private showDistrictsView(geojson: any, kind: MapKind, inventory: Map<string, GraphDistrict> | null): void {
    // Links first: Leaflet styles a feature before onEachFeature runs.
    for (const feature of geojson.features || []) {
      const props = feature.properties || (feature.properties = {});
      if (kind === 'cd') {
        props.__href = props.district_id ? `/districts/${props.district_id}/` : null;
        props.__tooltip = props.name || props.district_id;
        props.__key = props.district_id || '';
      } else {
        props.__href = sldHref(props, kind, inventory);
        props.__tooltip = sldTooltip(props, kind, props.__href);
        props.__key = `${props.state}-${props.code}`.toLowerCase();
      }
    }
    const self = this;
    this.districtLayer = L.geoJSON(geojson, {
      style: (feature: any) => (feature.properties.__href ? DEFAULT_STYLE : UNLINKED_STYLE),
      onEachFeature: (feature: any, layer: any) => {
        const { __href: href, __tooltip: tooltip, __key: key } = feature.properties;
        layer.bindTooltip(tooltip, { sticky: true, direction: 'top' });
        layer.on('add', function (this: any) {
          const el = this.getElement();
          if (!el) return;
          el.setAttribute('data-district', key);
          if (href) el.setAttribute('data-href', href);
          el.setAttribute('aria-label', tooltip);
        });
        layer.on('mouseover', function (this: any) {
          if (!href) return;
          this.setStyle(HOVER_STYLE);
          this.bringToFront();
          this.getElement().style.cursor = 'pointer';
        });
        layer.on('mouseout', function (this: any) { self.districtLayer?.resetStyle(this); });
        layer.on('click', () => { if (href) window.location.href = href; });
      },
    }).addTo(this.map);
    this.container.dataset.loaded = kind;
  }

  private setNotice(text: string | null): void {
    if (this.noticeEl) { this.noticeEl.remove(); this.noticeEl = null; }
    if (!text) return;
    this.noticeEl = document.createElement('div');
    this.noticeEl.className = 'map-notice';
    this.noticeEl.setAttribute('role', 'status');
    this.noticeEl.textContent = text;
    this.container.appendChild(this.noticeEl);
  }

  private addMapControls(stateName: string): void {
    this.removeMapControls();
    this.backBtnEl = document.createElement('button');
    this.backBtnEl.className = 'map-back-btn';
    this.backBtnEl.textContent = '← All States';
    this.backBtnEl.addEventListener('click', () => {
      writeURL({ state: null });
      this.loadStates().then(data => { if (data) this.showStatesView(); });
    });
    this.container.style.position = 'relative';
    this.container.appendChild(this.backBtnEl);
    this.stateLabelEl = document.createElement('div');
    this.stateLabelEl.className = 'map-state-label';
    this.stateLabelEl.textContent = stateName;
    this.container.appendChild(this.stateLabelEl);
  }

  private removeMapControls(): void {
    if (this.backBtnEl) { this.backBtnEl.remove(); this.backBtnEl = null; }
    if (this.stateLabelEl) { this.stateLabelEl.remove(); this.stateLabelEl = null; }
    this.setNotice(null);
  }

  // --- Relocated states (electinfo/rundeck#852) ---
  // The states view opens on the contiguous 48. Alaska, Hawaii and Puerto
  // Rico are drawn again, scaled and moved into open water in roughly their
  // true direction (the Albers-USA arrangement), with a dashed outline and a
  // label so nobody reads them as true position or size. DC stays where it
  // is but gets a labelled marker that can be hit at the national zoom. A
  // click on any of them fires the click of the state's real outline, so the
  // drill-in uses the real bounds. They show only at the national zoom levels.

  private realStateLayer(code: string): any {
    if (!this.statesLayer) return null;
    return this.statesLayer.getLayers().find((l: any) => l.feature && l.feature.properties.code === code) || null;
  }

  private insetLabel(code: string, latlng: [number, number]): any {
    const marker = L.marker(latlng, {
      interactive: true,
      keyboard: false,
      icon: L.divIcon({ className: '', html: '<div class="map-inset-label" data-state="' + code + '">' + code.toUpperCase() + '</div>', iconSize: null }),
    });
    marker.on('click', () => { this.realStateLayer(code)?.fire('click'); });
    return marker;
  }

  private addInsets(): void {
    this.removeInsets();
    const geojson = this.statesGeoJson;
    const defaultStyle = { fillColor: '#e2e8f0', weight: 1.5, color: '#1a365d', fillOpacity: 0.35, dashArray: '4 3' };
    const hoverStyle = { fillColor: '#ed8936', weight: 2.5, color: '#1a365d', fillOpacity: 0.55, dashArray: '4 3' };
    const layers: any[] = [];
    Object.keys(INSETS).forEach(code => {
      const feature = geojson.features.find((f: any) => f.properties.code === code);
      if (!feature) return;  // no shape published yet (PR until GeoShapeImageSync has run)
      const name = STATE_NAMES[code] || feature.properties.name;
      const shape = L.geoJSON(relocate(feature, INSETS[code]), {
        style: () => defaultStyle,
        onEachFeature: (_f: any, layer: any) => {
          layer.bindTooltip(name + ' (not to scale or position)', { sticky: true, direction: 'top' });
          layer.on('mouseover', function (this: any) { this.setStyle(hoverStyle); this.getElement().style.cursor = 'pointer'; });
          layer.on('mouseout', function (this: any) { this.setStyle(defaultStyle); });
          layer.on('click', () => { this.realStateLayer(code)?.fire('click'); });
        },
      });
      layers.push(shape, this.insetLabel(code, [INSETS[code].lat, INSETS[code].lon]));
    });
    const dc = geojson.features.find((f: any) => f.properties.code === 'dc');
    if (dc) {
      const c = L.geoJSON(dc).getBounds().getCenter();
      const ring = L.circleMarker(c, { radius: 7, color: '#1a365d', weight: 1.5, fillColor: '#e2e8f0', fillOpacity: 0.6, dashArray: '3 2' });
      ring.bindTooltip(STATE_NAMES.dc, { sticky: true, direction: 'top' });
      ring.on('click', () => { this.realStateLayer('dc')?.fire('click'); });
      layers.push(ring, this.insetLabel('dc', [c.lat - 0.9, c.lng + 1.6]));
    }
    if (!layers.length) return;
    this.insetGroup = L.layerGroup(layers);
    if (!this.insetZoomHooked) { this.insetZoomHooked = true; this.map.on('zoomend', () => this.syncInsets()); }
    this.syncInsets();
  }

  private syncInsets(): void {
    if (!this.insetGroup) return;
    const show = this.map.getZoom() <= INSET_MAX_ZOOM;
    if (show && !this.map.hasLayer(this.insetGroup)) this.insetGroup.addTo(this.map);
    if (!show && this.map.hasLayer(this.insetGroup)) this.map.removeLayer(this.insetGroup);
  }

  private removeInsets(): void {
    if (this.insetGroup) { if (this.map.hasLayer(this.insetGroup)) this.map.removeLayer(this.insetGroup); this.insetGroup = null; }
  }
}

interface InsetSpec { scale: number; lat: number; lon: number; minLon?: number }
const INSETS: Record<string, InsetSpec> = {
  ak: { scale: 0.32, lat: 27.5, lon: -119.5 },
  hi: { scale: 1.6, lat: 25.8, lon: -107.0, minLon: -161 },  // main islands only, not the chain to Midway
  pr: { scale: 2.8, lat: 26.8, lon: -73.5 },
};
const INSET_MAX_ZOOM = 5;

function mapCoords(coords: any, fn: (c: number[]) => any): any {
  if (typeof coords[0] === 'number') return fn(coords);
  return coords.map((c: any) => mapCoords(c, fn));
}

function relocate(feature: any, spec: InsetSpec): any {
  // Longitudes east of the antimeridian (the Aleutians) are brought west first.
  const west = (c: number[]) => [c[0] > 0 ? c[0] - 360 : c[0], c[1]];
  let parts = feature.geometry.type === 'MultiPolygon' ? feature.geometry.coordinates : [feature.geometry.coordinates];
  if (spec.minLon !== undefined) {
    parts = parts.filter((poly: any) => poly[0].some((c: number[]) => west(c)[0] >= spec.minLon!));
  }
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  mapCoords(parts, c => {
    const w = west(c);
    minLon = Math.min(minLon, w[0]); maxLon = Math.max(maxLon, w[0]);
    minLat = Math.min(minLat, w[1]); maxLat = Math.max(maxLat, w[1]);
    return c;
  });
  const cLon = (minLon + maxLon) / 2, cLat = (minLat + maxLat) / 2;
  const rad = (d: number) => d * Math.PI / 180;
  // Keep the real aspect: a degree of longitude is shorter at the state's
  // true latitude than at the inset's.
  const lonScale = spec.scale * Math.cos(rad(cLat)) / Math.cos(rad(spec.lat));
  const coords = mapCoords(parts, c => {
    const w = west(c);
    return [spec.lon + (w[0] - cLon) * lonScale, spec.lat + (w[1] - cLat) * spec.scale];
  });
  return { type: 'Feature', properties: feature.properties, geometry: { type: 'MultiPolygon', coordinates: coords } };
}

/** Replace URL parameters without a history entry (null removes one). */
function writeURL(changes: Record<string, string | null>): void {
  const url = new URL(location.href);
  for (const [key, value] of Object.entries(changes)) {
    if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
  }
  history.replaceState(history.state, '', url);
}

// --- Map | List toggle ---

function setup(): void {
  const mapViewEl = document.getElementById('map-view');
  const listViewEl = document.getElementById('list-view');
  const chamberBar = document.getElementById('map-chambers');
  if (!mapViewEl || !listViewEl || !chamberBar) return;
  const toggleBtns = document.querySelectorAll<HTMLElement>('.toggle-btn');
  const districtMap = new DistrictMap(mapViewEl, chamberBar);
  let listLoaded = false;

  function setView(view: 'map' | 'list'): void {
    if (view === 'map') {
      mapViewEl!.classList.remove('hidden');
      chamberBar!.classList.remove('hidden');
      listViewEl!.classList.add('hidden');
      writeURL({ view: 'map' });
      districtMap.show();
    } else {
      mapViewEl!.classList.add('hidden');
      chamberBar!.classList.add('hidden');
      listViewEl!.classList.remove('hidden');
      writeURL({ view: null });
      if (!listLoaded) {
        listLoaded = true;
        const script = document.createElement('script');
        script.src = '/js/district-index.js';
        document.body.appendChild(script);
      } else {
        // The map may have changed the category or state: the list re-reads the URL.
        window.dispatchEvent(new PopStateEvent('popstate', { state: history.state }));
      }
    }
    toggleBtns.forEach(btn => btn.classList.toggle('active', btn.dataset.view === view));
  }

  toggleBtns.forEach(btn => btn.addEventListener('click', () => setView(btn.dataset.view as 'map' | 'list')));

  // Entry is the list (with every category chip), unless the URL asks for the map.
  const params = new URLSearchParams(location.search);
  let view: 'map' | 'list' = params.get('view') === 'map' ? 'map' : 'list';
  const cycle = params.get('cycle');
  if (/^\d{4}$/.test(cycle || '') && Number(cycle) % 2 === 0 && Number(cycle) >= 1976 && Number(cycle) <= 2100) {
    // A boundary map has no activity membership; cycle mode uses only the
    // graph-backed, cycle-filtered list and its counts.
    view = 'list';
    (document.querySelector('[data-view="map"]') as HTMLElement | null)?.style.setProperty('display', 'none');
  }
  setView(view);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup);
  else setup();
}
